import React, { useState, useEffect, useCallback } from "react";
import campaignService from "../services/campaignService";

export interface IngredientPanelProps {
  onClose: () => void;
  onAddIngredient: (ingredientData: IngredientData) => Promise<void>;
  newsletterEditionId?: string;
  refreshKey?: number;
  onScrollToRow?: (uuid: string) => void;
}
export type LlmModelTier = 'balanced' | 'smart';
export type LlmProvider = 'anthropic' | 'openai';

//TODO: I think this will require more changes
export interface IngredientData {
  ingredientFormat: string;
  ingredientIds: string;
  instructions: string;
  model?: LlmModelTier;
  provider?: LlmProvider;
  useSelectivePlaceholderMerge?: boolean;
}

interface RequestStatus {
  isLoading: boolean;
  responseCode: number | null;
  message: string;
}

interface NewsletterIngredient {
  id: number;
  ingredient_id?: number;
  title: string;
  type: string;
  content_origin: string;
  num_uses: number;
  created_at: string;
  instructions?: string;
  row_uuid?: string;
}

interface SavedPrompt {
  id: string;
  name: string;
  content: string;
  category?: string;
  usage_count?: number;
  last_used_at?: string;
  created_at?: string;
  updated_at?: string;
}

interface BlueprintData {
  blueprint_id: string | null;
  title: string | null;
  operator_notes: string | null;
  audience: string | null;
  additional_details: string | null;
}

interface FormatOption {
  name: string;
  type: 'base' | 'custom';
  description: string;
  template_id?: number;
  ingredient_type?: string;
}

interface FrequentlyUsedInstruction {
  instructions: string;
  usage_count: number;
}

const IngredientPanel: React.FC<IngredientPanelProps> = ({ onClose, onAddIngredient, newsletterEditionId, refreshKey, onScrollToRow }) => {
  const hideFastCreate = true; // Set to false to show Fast Create button
  const [ingredientFormat, setIngredientFormat] = useState("Summary");
  const [ingredientIds, setIngredientIds] = useState("");
  const [instructions, setInstructions] = useState("");
  const [requestStatus, setRequestStatus] = useState<RequestStatus>({
    isLoading: false,
    responseCode: null,
    message: ""
  });
  const [newsletterIngredients, setNewsletterIngredients] = useState<NewsletterIngredient[]>([]);
  const [isLoadingNewsletterIngredients, setIsLoadingNewsletterIngredients] = useState(false);
  const [expandedPromptId, setExpandedPromptId] = useState<string | null>(null);
  const [editingPromptId, setEditingPromptId] = useState<string | null>(null);
  const [editedPromptContent, setEditedPromptContent] = useState<string>("");
  const [renamingPromptId, setRenamingPromptId] = useState<string | null>(null);
  const [editedPromptName, setEditedPromptName] = useState<string>("");
  const [savedPrompts, setSavedPrompts] = useState<SavedPrompt[]>([]);
  const [loadingSavedPrompts, setLoadingSavedPrompts] = useState(false);
  const [savingPrompt, setSavingPrompt] = useState(false);
  const [blueprintData, setBlueprintData] = useState<BlueprintData | null>(null);
  const [loadingBlueprintData, setLoadingBlueprintData] = useState(false);
  const [model, setModel] = useState<LlmModelTier>('smart'); // Default: highest quality
  const [provider, setProvider] = useState<LlmProvider>('anthropic'); // Default: Claude
  const [useSelectivePlaceholderMerge, setUseSelectivePlaceholderMerge] = useState(false);
  const [formatOptions, setFormatOptions] = useState<FormatOption[]>([
    { name: "Summary", type: "base", description: "Standard summary format" },
    { name: "Link Dump", type: "base", description: "Link dump format" },
  ]);
  const [isLoadingFormats, setIsLoadingFormats] = useState(false);
  const [expandedUsedIngredientId, setExpandedUsedIngredientId] = useState<number | null>(null);
  const [frequentlyUsedInstructions, setFrequentlyUsedInstructions] = useState<FrequentlyUsedInstruction[]>([]);
  const [isLoadingFrequentlyUsed, setIsLoadingFrequentlyUsed] = useState(false);

  // Generate a short name for the prompt content
  const generatePromptName = (content: string): string => {
    const words = content.trim().split(/\s+/);

    // Take first 3-4 meaningful words, avoiding common words
    const stopWords = new Set(['a', 'an', 'the', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by', 'write', 'include', 'provide', 'use', 'make', 'ensure']);
    const meaningfulWords = words.filter(word =>
      word.length > 2 && !stopWords.has(word.toLowerCase())
    ).slice(0, 3);

    if (meaningfulWords.length === 0) {
      return `Prompt ${Date.now().toString().slice(-6)}`;
    }

    // Capitalize first letter of each word
    return meaningfulWords
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Handler for clicking a saved prompt
  const handlePromptClick = async (prompt: SavedPrompt) => {
    if (editingPromptId === prompt.id) return; // Don't populate if editing
    setInstructions(prompt.content);

    // Track usage
    try {
      await campaignService.trackSavedIngredientPromptUsage(prompt.id);
      // Refresh the prompts to update usage count
      fetchSavedPrompts();
    } catch (error) {
      console.error('Failed to track prompt usage:', error);
    }
  };

  // Handler for editing a saved prompt
  const handleEditPrompt = (promptId: string) => {
    const prompt = savedPrompts.find(p => p.id === promptId);
    if (prompt) {
      setEditingPromptId(promptId);
      setEditedPromptContent(prompt.content);
    }
  };

  // Handler for saving an edited prompt
  const handleSaveEditedPrompt = async (promptId: string) => {
    try {
      await campaignService.updateSavedIngredientPrompt(promptId, {
        content: editedPromptContent
      });
      setEditingPromptId(null);
      setEditedPromptContent("");
      fetchSavedPrompts();
    } catch (error) {
      console.error('Failed to update prompt:', error);
    }
  };

  // Handler for canceling prompt edit
  const handleCancelEdit = () => {
    setEditingPromptId(null);
    setEditedPromptContent("");
  };

  // Handler for starting rename
  const handleStartRename = (promptId: string) => {
    const prompt = savedPrompts.find(p => p.id === promptId);
    if (prompt) {
      setRenamingPromptId(promptId);
      setEditedPromptName(prompt.name);
    }
  };

  // Handler for saving renamed prompt
  const handleSaveRename = async (promptId: string) => {
    if (!editedPromptName.trim()) return;

    try {
      await campaignService.updateSavedIngredientPrompt(promptId, {
        name: editedPromptName.trim()
      });
      setRenamingPromptId(null);
      setEditedPromptName("");
      fetchSavedPrompts();
    } catch (error) {
      console.error('Failed to rename prompt:', error);
    }
  };

  // Handler for canceling rename
  const handleCancelRename = () => {
    setRenamingPromptId(null);
    setEditedPromptName("");
  };

  // Handler for deleting a saved prompt
  const handleDeletePrompt = async (promptId: string) => {
    const prompt = savedPrompts.find(p => p.id === promptId);
    if (prompt && window.confirm(`Are you sure you want to delete "${prompt.name}"?`)) {
      try {
        await campaignService.deleteSavedIngredientPrompt(promptId);
        // Reset expanded/editing states if this prompt was being edited
        if (expandedPromptId === promptId) {
          setExpandedPromptId(null);
        }
        if (editingPromptId === promptId) {
          setEditingPromptId(null);
          setEditedPromptContent("");
        }
        fetchSavedPrompts();
      } catch (error) {
        console.error('Failed to delete prompt:', error);
      }
    }
  };

  // Handler for saving a new prompt
  const handleSavePrompt = async () => {
    if (!instructions.trim()) return;

    setSavingPrompt(true);
    try {
      const name = generatePromptName(instructions);
      await campaignService.createSavedIngredientPrompt(name, instructions.trim());
      fetchSavedPrompts();
    } catch (error) {
      console.error('Failed to save prompt:', error);
    } finally {
      setSavingPrompt(false);
    }
  };

  // Fetch saved prompts
  const fetchSavedPrompts = async () => {
    setLoadingSavedPrompts(true);
    try {
      const prompts = await campaignService.getSavedIngredientPrompts();
      setSavedPrompts(prompts);
    } catch (error) {
      console.error('Failed to fetch saved prompts:', error);
      setSavedPrompts([]);
    } finally {
      setLoadingSavedPrompts(false);
    }
  };

  // Fetch blueprint data
  const fetchBlueprintData = useCallback(async () => {
    if (!newsletterEditionId) return;

    setLoadingBlueprintData(true);
    try {
      const data = await campaignService.getNewsletterEditionBlueprint(newsletterEditionId);
      setBlueprintData(data);
    } catch (error) {
      console.error('Failed to fetch blueprint data:', error);
      setBlueprintData(null);
    } finally {
      setLoadingBlueprintData(false);
    }
  }, [newsletterEditionId]);

  // Fetch frequently used instructions
  const fetchFrequentlyUsedInstructions = async () => {
    setIsLoadingFrequentlyUsed(true);
    try {
      const response = await campaignService.getFrequentlyUsedInstructions();
      setFrequentlyUsedInstructions(response.frequently_used || []);
    } catch (error) {
      console.error('Failed to fetch frequently used instructions:', error);
      setFrequentlyUsedInstructions([]);
    } finally {
      setIsLoadingFrequentlyUsed(false);
    }
  };

  // Fetch used ingredients when component mounts, newsletterEditionId changes, or refreshKey changes
  useEffect(() => {
    const fetchUsedIngredients = async () => {
      if (!newsletterEditionId) return;

      setIsLoadingNewsletterIngredients(true);
      try {
        const response = await campaignService.getNewsletterEditionIngredients(newsletterEditionId);
        setNewsletterIngredients(response.ingredients || []);
      } catch (error) {
        console.error('Failed to fetch used ingredients:', error);
        setNewsletterIngredients([]);
      } finally {
        setIsLoadingNewsletterIngredients(false);
      }
    };

    fetchUsedIngredients();
  }, [newsletterEditionId, refreshKey]);

  // Fetch saved prompts when component mounts
  useEffect(() => {
    fetchSavedPrompts();
  }, []);

  // Fetch frequently used instructions when component mounts
  useEffect(() => {
    fetchFrequentlyUsedInstructions();
  }, []);

  // Fetch blueprint data when newsletterEditionId changes
  useEffect(() => {
    fetchBlueprintData();
  }, [newsletterEditionId, fetchBlueprintData]);

  // Fetch available ingredient formats when blueprint data changes
  useEffect(() => {
    const fetchFormats = async () => {
      if (!newsletterEditionId) return;

      setIsLoadingFormats(true);
      try {
        const response = await campaignService.getAvailableIngredientFormats(blueprintData?.blueprint_id);
        if (response.formats && response.formats.length > 0) {
          setFormatOptions(response.formats);
        }
      } catch (error) {
        console.error('Failed to fetch available formats:', error);
        // Keep default formats on error
      } finally {
        setIsLoadingFormats(false);
      }
    };

    fetchFormats();
  }, [newsletterEditionId, blueprintData?.blueprint_id]);

  // Get placeholder text based on selected format
  const getPlaceholderText = (format: string) => {
    switch (format) {
      case "Summary":
        return "1124";
      case "Link Dump":
        return "1124, 123, 567";
      case "Rotating Content":
        return "1124";
      default:
        return "1124";
    }
  };

  const handleAdd = async () => {
    setRequestStatus({
      isLoading: true,
      responseCode: null,
      message: "Processing request..."
    });

    try {
      await onAddIngredient({
        ingredientFormat,
        ingredientIds,
        instructions,
        model,
        provider,
        useSelectivePlaceholderMerge
      });

      setRequestStatus({
        isLoading: false,
        responseCode: 200,
        message: "Successfully added ingredient"
      });

      // Reset form after successful submission
      setIngredientFormat("Summary");
      setIngredientIds("");
      setInstructions("");

      // Clear status after 3 seconds
      setTimeout(() => {
        setRequestStatus({ isLoading: false, responseCode: null, message: "" });
      }, 3000);

    } catch (error: any) {
      const responseCode = error?.response?.status || 500;
      const message = error?.response?.data?.error || error?.message || "Request failed";

      setRequestStatus({
        isLoading: false,
        responseCode,
        message: `Error ${responseCode}: ${message}`
      });

      // Clear error after 5 seconds
      setTimeout(() => {
        setRequestStatus({ isLoading: false, responseCode: null, message: "" });
      }, 5000);
    }
  };

  const handleFastCreate = async () => {
    if (!newsletterEditionId) {
      setRequestStatus({
        isLoading: false,
        responseCode: 400,
        message: "Newsletter edition ID is required for fast create"
      });
      return;
    }

    setRequestStatus({
      isLoading: true,
      responseCode: null,
      message: "Starting fast create job..."
    });

    try {
      // Start the async job
      const response = await campaignService.fastCreateIngredients(newsletterEditionId);
      const jobId = response.job_id;

      if (!jobId) {
        throw new Error("No job ID returned from fast create");
      }

      // Poll for completion
      const pollForCompletion = async () => {
        try {
          const statusResponse = await campaignService.pollFastCreateStatus(jobId);

          if (statusResponse.status === 'completed') {
            setRequestStatus({
              isLoading: false,
              responseCode: 200,
              message: statusResponse.message || "Successfully added 3 content rows"
            });

            // Refresh the page after a short delay to show success message
            setTimeout(() => {
              window.location.reload();
            }, 1500);

          } else if (statusResponse.status === 'failed') {
            setRequestStatus({
              isLoading: false,
              responseCode: 500,
              message: `Fast create failed: ${statusResponse.error}`
            });

            // Clear error after 5 seconds
            setTimeout(() => {
              setRequestStatus({ isLoading: false, responseCode: null, message: "" });
            }, 5000);

          } else {
            // Still processing, update status and continue polling
            setRequestStatus({
              isLoading: true,
              responseCode: null,
              message: statusResponse.message || "Processing fast create..."
            });

            setTimeout(pollForCompletion, 2000); // Poll every 2 seconds
          }

        } catch (pollError: any) {
          console.error("Error polling fast create status:", pollError);
          setRequestStatus({
            isLoading: false,
            responseCode: 500,
            message: "Error checking fast create status"
          });

          setTimeout(() => {
            setRequestStatus({ isLoading: false, responseCode: null, message: "" });
          }, 5000);
        }
      };

      // Start polling
      setTimeout(pollForCompletion, 1000); // Start polling after 1 second

    } catch (error: any) {
      const responseCode = error?.response?.status || 500;
      const message = error?.response?.data?.error || error?.message || "Fast create failed";

      setRequestStatus({
        isLoading: false,
        responseCode,
        message: `Error ${responseCode}: ${message}`
      });

      // Clear error after 5 seconds
      setTimeout(() => {
        setRequestStatus({ isLoading: false, responseCode: null, message: "" });
      }, 5000);
    }
  };

  // Helper function to get status color
  const getStatusColor = (responseCode: number | null, isLoading: boolean) => {
    if (isLoading) return "#6366f1"; // Blue for loading
    if (!responseCode) return "transparent";
    if (responseCode >= 200 && responseCode < 300) return "#10b981"; // Green for success
    if (responseCode >= 400 && responseCode < 500) return "#f59e0b"; // Orange for client errors
    if (responseCode >= 500) return "#ef4444"; // Red for server errors
    return "#6b7280"; // Gray for unknown
  };

  // Helper function to get status background color (lighter version)
  const getStatusBackgroundColor = (responseCode: number | null, isLoading: boolean) => {
    if (isLoading) return "#e0e7ff"; // Light blue for loading
    if (!responseCode) return "transparent";
    if (responseCode >= 200 && responseCode < 300) return "#d1fae5"; // Light green for success
    if (responseCode >= 400 && responseCode < 500) return "#fef3c7"; // Light orange for client errors
    if (responseCode >= 500) return "#fee2e2"; // Light red for server errors
    return "#f3f4f6"; // Light gray for unknown
  };

  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <div style={{
        height: "100vh",
        overflowY: "scroll",
      }}>
        <h1>Ingredients (alpha)</h1>

        <div style={{
          backgroundColor: "#fff3cd",
          border: "1px solid #ffeaa7",
          borderRadius: "4px",
          padding: "12px",
          marginBottom: "20px",
          fontSize: "14px"
        }}>
          <strong>NOTE:</strong> This feature is under development and only intended for use by the Potions team. External customers should ignore it.
        </div>

        {/* Used Ingredients Section */}
        <div style={{ marginBottom: "24px" }}>
          <h3 style={{ margin: "0 0 12px 0", fontSize: "16px", fontWeight: "600" }}>Currently Used Ingredients</h3>
          {isLoadingNewsletterIngredients ? (
            <div style={{
              padding: "12px",
              backgroundColor: "#f8f9fa",
              borderRadius: "6px",
              fontSize: "14px",
              color: "#6b7280",
              textAlign: "center"
            }}>
              Loading used ingredients...
            </div>
          ) : newsletterIngredients.length === 0 ? (
            <div style={{
              padding: "12px",
              backgroundColor: "#f8f9fa",
              borderRadius: "6px",
              fontSize: "14px",
              color: "#6b7280",
              textAlign: "center"
            }}>
              No ingredients currently used in this newsletter
            </div>
          ) : (
            <div style={{
              backgroundColor: "#ffffff",
              border: "1px solid #e5e7eb",
              borderRadius: "6px",
              maxHeight: "300px",
              overflowY: "auto"
            }}>
              {newsletterIngredients.map((ingredient, index) => (
                <div
                  key={ingredient.id}
                  style={{
                    borderBottom: index < newsletterIngredients.length - 1 ? "1px solid #f3f4f6" : "none",
                  }}
                >
                  <div
                    onClick={() => ingredient.instructions && setExpandedUsedIngredientId(
                      expandedUsedIngredientId === ingredient.id ? null : ingredient.id
                    )}
                    style={{
                      padding: "12px 16px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      cursor: ingredient.instructions ? "pointer" : "default",
                      backgroundColor: expandedUsedIngredientId === ingredient.id ? "#f9fafb" : "transparent",
                      transition: "background-color 0.2s"
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#111827",
                        marginBottom: "4px",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px"
                      }}>
                        {ingredient.title}
                      </div>
                      <div style={{
                        fontSize: "12px",
                        color: "#6b7280"
                      }}>
                        ID: {ingredient.id} • {ingredient.type} • Used {ingredient.num_uses} time{ingredient.num_uses !== 1 ? 's' : ''}
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      {ingredient.row_uuid && onScrollToRow && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onScrollToRow(ingredient.row_uuid!);
                          }}
                          style={{
                            fontSize: "11px",
                            padding: "2px 8px",
                            backgroundColor: "#e0e7ff",
                            color: "#3730a3",
                            border: "1px solid #a5b4fc",
                            borderRadius: "12px",
                            fontWeight: "500",
                            cursor: "pointer",
                          }}
                          title="Scroll to this ingredient's content row in the editor"
                        >
                          View in Editor
                        </button>
                      )}
                      <div style={{
                        fontSize: "11px",
                        padding: "2px 8px",
                        backgroundColor: ingredient.content_origin === "original_content" ? "#dbeafe" : "#f3f4f6",
                        color: ingredient.content_origin === "original_content" ? "#1e40af" : "#6b7280",
                        borderRadius: "12px",
                        fontWeight: "500"
                      }}>
                        {ingredient.content_origin === "original_content" ? "Original" : "External"}
                      </div>
                      {ingredient.instructions && (
                        <span style={{
                          fontSize: "12px",
                          color: "#6b7280",
                          transition: "transform 0.2s",
                          transform: expandedUsedIngredientId === ingredient.id ? "rotate(90deg)" : "rotate(0deg)"
                        }}>
                          ▶
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Expanded Instructions Section */}
                  {expandedUsedIngredientId === ingredient.id && ingredient.instructions && (
                    <div style={{
                      padding: "0 16px 12px 16px",
                      backgroundColor: "#f9fafb"
                    }}>
                      <div style={{
                        backgroundColor: "#ffffff",
                        border: "1px solid #e5e7eb",
                        borderRadius: "6px",
                        padding: "12px"
                      }}>
                        <div style={{
                          fontSize: "11px",
                          fontWeight: "600",
                          color: "#6b7280",
                          marginBottom: "6px",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px"
                        }}>
                          Instructions Used
                        </div>
                        <div style={{
                          fontSize: "13px",
                          color: "#374151",
                          lineHeight: "1.5",
                          whiteSpace: "pre-wrap",
                          marginBottom: "10px"
                        }}>
                          {ingredient.instructions}
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setInstructions(ingredient.instructions || '');
                            setIngredientIds(ingredient.id.toString());
                          }}
                          style={{
                            padding: "6px 12px",
                            fontSize: "12px",
                            backgroundColor: "#10b981",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontWeight: "500"
                          }}
                        >
                          Use This Instruction
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <p className="blueprint-section-prop">
          <b>Ingredient Format</b>
          <select
            value={ingredientFormat}
            onChange={(e) => setIngredientFormat(e.target.value)}
            className="blueprint-section-dropdown"
            disabled={isLoadingFormats}
          >
            {isLoadingFormats ? (
              <option value="">Loading formats...</option>
            ) : (
              <>
                {formatOptions.filter(opt => opt.type === 'base').length > 0 && (
                  <optgroup label="Base Formats">
                    {formatOptions.filter(opt => opt.type === 'base').map((option) => (
                      <option key={option.name} value={option.name} title={option.description}>
                        {option.name}
                      </option>
                    ))}
                  </optgroup>
                )}
                {formatOptions.filter(opt => opt.type === 'custom').length > 0 && (
                  <optgroup label="Custom Templates">
                    {formatOptions.filter(opt => opt.type === 'custom').map((option) => (
                      <option key={option.name} value={option.name} title={option.description}>
                        {option.name}
                      </option>
                    ))}
                  </optgroup>
                )}
              </>
            )}
          </select>
        </p>

        <p className="blueprint-section-prop">
          <b style={{ textAlign: "left" }}>Ingredient ID(s)</b>
          <input
            type="text"
            value={ingredientIds}
            onChange={(e) => setIngredientIds(e.target.value)}
            placeholder={getPlaceholderText(ingredientFormat)}
            style={{
              width: "280px",
              padding: "8px 12px",
              borderRadius: "4px",
              border: "1px solid #ccc",
              backgroundColor: "#ffffff",
              fontSize: "14px",
              color: "#333",
              cursor: "text",
              outline: "none",
              transition: "all 0.2s ease",
              boxSizing: "border-box",
            }}
          />
        </p>

        <p className="blueprint-section-prop">
          <b style={{ textAlign: "left" }}>Instructions</b>
        </p>
        <div style={{ padding: "0 16px" }}>
          <textarea
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            placeholder="Add instructions for this ingredient..."
            rows={6}
            cols={30}
            className="feedback-textarea-input"
            style={{ marginBottom: "20px" }}
          />
        </div>

        {/* Provider + Model tier pickers */}
        <div style={{ padding: "0 16px", marginBottom: "16px", display: "flex", flexDirection: "column", gap: "12px" }}>
          <label style={{ display: "flex", flexDirection: "column", gap: "6px", fontSize: "14px" }}>
            <span style={{ color: "#374151", fontWeight: 500 }}>Provider</span>
            <select
              value={provider}
              onChange={(e) => setProvider(e.target.value as LlmProvider)}
              style={{
                padding: "8px 10px",
                fontSize: "14px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                backgroundColor: "#fff",
                cursor: "pointer",
              }}
            >
              <option value="anthropic">Anthropic (Claude)</option>
              <option value="openai">OpenAI (GPT)</option>
            </select>
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: "6px", fontSize: "14px" }}>
            <span style={{ color: "#374151", fontWeight: 500 }}>Model</span>
            <select
              value={model}
              onChange={(e) => setModel(e.target.value as LlmModelTier)}
              style={{
                padding: "8px 10px",
                fontSize: "14px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                backgroundColor: "#fff",
                cursor: "pointer",
              }}
            >
              <option value="balanced">
                Balanced — mid-tier quality ({provider === 'anthropic' ? 'claude-sonnet' : 'gpt-4o'})
              </option>
              <option value="smart">
                Smart — highest quality, slower ({provider === 'anthropic' ? 'claude-opus' : 'gpt-4.1'})
              </option>
            </select>
          </label>
        </div>

        {/* Selective Placeholder Merge Checkbox */}
        <div style={{ padding: "0 16px", marginBottom: "16px" }}>
          <label style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            fontSize: "14px",
            cursor: "pointer"
          }}>
            <input
              type="checkbox"
              checked={useSelectivePlaceholderMerge}
              onChange={(e) => setUseSelectivePlaceholderMerge(e.target.checked)}
              style={{
                width: "16px",
                height: "16px",
                cursor: "pointer"
              }}
            />
            <span style={{ color: "#374151" }}>
              Use Selective Placeholder Merge <span style={{ fontSize: "12px", color: "#6b7280" }}>(Experimental)</span>
            </span>
          </label>
        </div>

        {/* Status Display */}
        {(requestStatus.isLoading || requestStatus.responseCode) && (
          <div style={{
            padding: "0 16px",
            marginBottom: "16px"
          }}>
            <div style={{
              backgroundColor: getStatusBackgroundColor(requestStatus.responseCode, requestStatus.isLoading),
              border: `1px solid ${getStatusColor(requestStatus.responseCode, requestStatus.isLoading)}`,
              borderRadius: "6px",
              padding: "12px",
              fontSize: "14px",
              color: getStatusColor(requestStatus.responseCode, requestStatus.isLoading),
              display: "flex",
              alignItems: "center",
              gap: "8px"
            }}>
              {requestStatus.isLoading && (
                <div style={{
                  width: "16px",
                  height: "16px",
                  border: "2px solid transparent",
                  borderTop: `2px solid ${getStatusColor(requestStatus.responseCode, requestStatus.isLoading)}`,
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite"
                }} />
              )}
              <span>
                {requestStatus.responseCode && !requestStatus.isLoading && (
                  <strong>[{requestStatus.responseCode}] </strong>
                )}
                {requestStatus.message}
              </span>
            </div>
          </div>
        )}

        <div style={{ padding: "0 16px", marginBottom: "20px" }}>
          {/* Fast Create Button - conditionally rendered */}
          {!hideFastCreate && (
            <>
              <button
                onClick={handleFastCreate}
                disabled={requestStatus.isLoading || !newsletterEditionId}
                style={{
                  backgroundColor: requestStatus.isLoading || !newsletterEditionId ? "#9ca3af" : "#f59e0b",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  padding: "14px 24px",
                  fontSize: "16px",
                  cursor: requestStatus.isLoading || !newsletterEditionId ? "not-allowed" : "pointer",
                  width: "100%",
                  fontWeight: "600",
                  opacity: requestStatus.isLoading || !newsletterEditionId ? 0.6 : 1,
                  marginBottom: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px"
                }}
              >
                <span style={{ fontSize: "18px" }}>⚡</span>
                {requestStatus.isLoading ? "Creating 3 rows..." : "Fast Create (3 Auto Rows)"}
              </button>

              <div style={{
                borderTop: "1px solid #e5e7eb",
                margin: "16px 0",
                position: "relative"
              }}>
                <span style={{
                  position: "absolute",
                  top: "-10px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  backgroundColor: "#f9fafb",
                  padding: "0 8px",
                  fontSize: "12px",
                  color: "#9ca3af"
                }}>
                  OR
                </span>
              </div>
            </>
          )}

          {/* Existing buttons */}
          <div style={{ display: "flex", gap: "8px", marginTop: hideFastCreate ? "0" : "16px" }}>
            <button
              onClick={handleAdd}
              disabled={requestStatus.isLoading}
              style={{
                backgroundColor: requestStatus.isLoading ? "#9ca3af" : "#6366f1",
                color: "white",
                border: "none",
                borderRadius: "8px",
                padding: "12px 24px",
                fontSize: "16px",
                cursor: requestStatus.isLoading ? "not-allowed" : "pointer",
                flex: 1,
                fontWeight: "500",
                opacity: requestStatus.isLoading ? 0.6 : 1,
              }}
            >
              {requestStatus.isLoading ? "Processing..." : "Add"}
            </button>
            <button
              onClick={handleSavePrompt}
              disabled={!instructions.trim() || savingPrompt}
              style={{
                backgroundColor: (instructions.trim() && !savingPrompt) ? "#10b981" : "#9ca3af",
                color: "white",
                border: "none",
                borderRadius: "8px",
                padding: "12px 20px",
                fontSize: "16px",
                cursor: (instructions.trim() && !savingPrompt) ? "pointer" : "not-allowed",
                fontWeight: "500",
                opacity: (instructions.trim() && !savingPrompt) ? 1 : 0.6,
              }}
            >
              {savingPrompt ? "Saving..." : "Save Prompt"}
            </button>
          </div>
        </div>

        {/* Saved Prompts Section */}
        <div style={{ padding: "0 16px" }}>
          <h3 style={{ margin: "0 0 12px 0", fontSize: "16px", fontWeight: "600" }}>Saved Prompts</h3>
          {loadingSavedPrompts ? (
            <div style={{
              padding: "12px",
              backgroundColor: "#f8f9fa",
              borderRadius: "6px",
              fontSize: "14px",
              color: "#6b7280",
              textAlign: "center"
            }}>
              Loading saved prompts...
            </div>
          ) : savedPrompts.length === 0 ? (
            <div style={{
              padding: "12px",
              backgroundColor: "#f8f9fa",
              borderRadius: "6px",
              fontSize: "14px",
              color: "#6b7280",
              textAlign: "center"
            }}>
              No saved prompts yet. Create your first one by adding instructions and clicking "Save Prompt".
            </div>
          ) : (
            <div style={{
              backgroundColor: "#ffffff",
              border: "1px solid #e5e7eb",
              borderRadius: "6px",
            }}>
              {savedPrompts.map((prompt, index) => (
                <div
                  key={prompt.id}
                  style={{
                    borderBottom: index < savedPrompts.length - 1 ? "1px solid #f3f4f6" : "none",
                  }}
                >
                  <div
                    onClick={() => !editingPromptId && !renamingPromptId && handlePromptClick(prompt)}
                    style={{
                      padding: "12px 16px",
                      cursor: (editingPromptId === prompt.id || renamingPromptId === prompt.id) ? "default" : "pointer",
                      backgroundColor: editingPromptId === prompt.id ? "#f9fafb" : "transparent",
                      transition: "background-color 0.2s",
                      ...(editingPromptId !== prompt.id && !expandedPromptId && {
                        ":hover": { backgroundColor: "#f9fafb" }
                      })
                    }}
                    onMouseEnter={(e) => {
                      if (editingPromptId !== prompt.id && !expandedPromptId) {
                        e.currentTarget.style.backgroundColor = "#f9fafb";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (editingPromptId !== prompt.id) {
                        e.currentTarget.style.backgroundColor = "transparent";
                      }
                    }}
                  >
                    <div style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center"
                    }}>
                      <div style={{
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#111827",
                        flex: 1
                      }}>
                        {renamingPromptId === prompt.id ? (
                          <input
                            type="text"
                            value={editedPromptName}
                            onChange={(e) => setEditedPromptName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleSaveRename(prompt.id);
                              } else if (e.key === 'Escape') {
                                handleCancelRename();
                              }
                            }}
                            onBlur={() => handleSaveRename(prompt.id)}
                            onClick={(e) => e.stopPropagation()}
                            autoFocus
                            style={{
                              fontSize: "14px",
                              fontWeight: "500",
                              color: "#111827",
                              backgroundColor: "white",
                              border: "1px solid #3b82f6",
                              borderRadius: "4px",
                              padding: "2px 6px",
                              width: "100%",
                              outline: "none"
                            }}
                          />
                        ) : (
                          prompt.name
                        )}
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedPromptId(expandedPromptId === prompt.id ? null : prompt.id);
                        }}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          padding: "4px",
                          fontSize: "12px",
                          color: "#6b7280"
                        }}
                      >
                        {expandedPromptId === prompt.id ? "▼" : "▶"}
                      </button>
                    </div>

                    {/* Expanded content */}
                    {expandedPromptId === prompt.id && (
                      <div style={{
                        marginTop: "8px",
                        paddingTop: "8px",
                        borderTop: "1px solid #f3f4f6"
                      }}>
                        {editingPromptId === prompt.id ? (
                          <div>
                            <textarea
                              value={editedPromptContent}
                              onChange={(e) => setEditedPromptContent(e.target.value)}
                              rows={3}
                              style={{
                                width: "100%",
                                padding: "8px",
                                fontSize: "13px",
                                border: "1px solid #d1d5db",
                                borderRadius: "4px",
                                resize: "vertical"
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <div style={{
                              marginTop: "8px",
                              display: "flex",
                              gap: "8px"
                            }}>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSaveEditedPrompt(prompt.id);
                                }}
                                style={{
                                  padding: "4px 12px",
                                  fontSize: "12px",
                                  backgroundColor: "#6366f1",
                                  color: "white",
                                  border: "none",
                                  borderRadius: "4px",
                                  cursor: "pointer"
                                }}
                              >
                                Save
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCancelEdit();
                                }}
                                style={{
                                  padding: "4px 12px",
                                  fontSize: "12px",
                                  backgroundColor: "#f3f4f6",
                                  color: "#6b7280",
                                  border: "1px solid #d1d5db",
                                  borderRadius: "4px",
                                  cursor: "pointer"
                                }}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div>
                            <div style={{
                              fontSize: "13px",
                              color: "#6b7280",
                              lineHeight: "1.5",
                              marginBottom: "8px"
                            }}>
                              {prompt.content}
                            </div>
                            {prompt.usage_count !== undefined && (
                              <div style={{
                                fontSize: "11px",
                                color: "#9ca3af",
                                marginBottom: "8px"
                              }}>
                                Used {prompt.usage_count} time{prompt.usage_count !== 1 ? 's' : ''}
                                {prompt.last_used_at && (
                                  <span> • Last used: {new Date(prompt.last_used_at).toLocaleDateString()}</span>
                                )}
                              </div>
                            )}
                            <div style={{
                              marginTop: "8px",
                              display: "flex",
                              gap: "8px"
                            }}>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleStartRename(prompt.id);
                                }}
                                style={{
                                  padding: "4px 12px",
                                  fontSize: "12px",
                                  backgroundColor: "#e0f2fe",
                                  color: "#0369a1",
                                  border: "1px solid #7dd3fc",
                                  borderRadius: "4px",
                                  cursor: "pointer"
                                }}
                              >
                                Rename
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditPrompt(prompt.id);
                                }}
                                style={{
                                  padding: "4px 12px",
                                  fontSize: "12px",
                                  backgroundColor: "#f3f4f6",
                                  color: "#4b5563",
                                  border: "1px solid #d1d5db",
                                  borderRadius: "4px",
                                  cursor: "pointer"
                                }}
                              >
                                Edit
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeletePrompt(prompt.id);
                                }}
                                style={{
                                  padding: "4px 12px",
                                  fontSize: "12px",
                                  backgroundColor: "#fee2e2",
                                  color: "#dc2626",
                                  border: "1px solid #fca5a5",
                                  borderRadius: "4px",
                                  cursor: "pointer",
                                  transition: "all 0.2s"
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = "#fca5a5";
                                  e.currentTarget.style.color = "#991b1b";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = "#fee2e2";
                                  e.currentTarget.style.color = "#dc2626";
                                }}
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Frequently Used Instructions Section */}
        <div style={{ padding: "0 16px", marginTop: "24px" }}>
          <h3 style={{ margin: "0 0 12px 0", fontSize: "16px", fontWeight: "600" }}>
            Frequently Used Instructions
          </h3>
          {isLoadingFrequentlyUsed ? (
            <div style={{
              padding: "12px",
              backgroundColor: "#f8f9fa",
              borderRadius: "6px",
              fontSize: "14px",
              color: "#6b7280",
              textAlign: "center"
            }}>
              Loading frequently used instructions...
            </div>
          ) : frequentlyUsedInstructions.length === 0 ? (
            <div style={{
              padding: "12px",
              backgroundColor: "#f8f9fa",
              borderRadius: "6px",
              fontSize: "14px",
              color: "#6b7280",
              textAlign: "center"
            }}>
              No frequently used instructions yet. Instructions will appear here as you use them.
            </div>
          ) : (
            <div style={{
              backgroundColor: "#ffffff",
              border: "1px solid #e5e7eb",
              borderRadius: "6px",
            }}>
              {frequentlyUsedInstructions.map((item, index) => (
                <div
                  key={index}
                  onClick={() => setInstructions(item.instructions)}
                  style={{
                    padding: "12px 16px",
                    borderBottom: index < frequentlyUsedInstructions.length - 1 ? "1px solid #f3f4f6" : "none",
                    cursor: "pointer",
                    transition: "background-color 0.2s"
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#f9fafb";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }}
                >
                  <div style={{
                    fontSize: "13px",
                    color: "#374151",
                    lineHeight: "1.5",
                    marginBottom: "6px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical"
                  }}>
                    {item.instructions}
                  </div>
                  <div style={{
                    fontSize: "11px",
                    color: "#9ca3af"
                  }}>
                    Used {item.usage_count} time{item.usage_count !== 1 ? 's' : ''}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Operator Notes Section */}
        {(blueprintData?.operator_notes || loadingBlueprintData) && (
          <div style={{
            padding: "16px",
            marginTop: "24px",
            borderTop: "1px solid #e5e7eb"
          }}>
            <h3 style={{ margin: "0 0 12px 0", fontSize: "16px", fontWeight: "600" }}>
              Operator Notes
            </h3>
            {loadingBlueprintData ? (
              <div style={{
                padding: "12px",
                backgroundColor: "#f8f9fa",
                borderRadius: "6px",
                fontSize: "14px",
                color: "#6b7280",
                textAlign: "center"
              }}>
                Loading operator notes...
              </div>
            ) : blueprintData?.operator_notes ? (
              <div style={{
                backgroundColor: "#fffbeb",
                border: "1px solid #fed7aa",
                borderRadius: "6px",
                padding: "16px",
                fontSize: "14px",
                lineHeight: "1.5",
                color: "#92400e",
                whiteSpace: "pre-wrap"
              }}>
                {blueprintData.operator_notes}
              </div>
            ) : null}
          </div>
        )}
      </div>
    </>
  );
};

export default IngredientPanel;